/**
 * FilingContext — typed view of the backend `filing_context` sub-object
 * emitted by LL97 / LL88 submission serializers.
 *
 * This shape is an **additive-only contract**. Every field below (other than
 * the known-required ones) is optional because:
 *
 *   1. The backend reserves the right to ship new fields without a version
 *      bump (see filing_context_api_architecture.md §2.8).
 *   2. Stale API responses in the wild may predate Phase 1 and omit the
 *      `filingContext` block entirely. Consumers must tolerate that.
 *
 * Required-in-principle by the contract: `law.code`, `cycle.calendarYear`,
 * `cycle.standardDeadline`, `status.code`, `status.label`, `status.bucket`.
 * Everything else is defensive `?` so a missing field never throws at the
 * typesystem boundary.
 *
 * Field names here are camelCase — the established consumer contract (21
 * call sites read `standardDeadline`, `extensionFeeCents`, …). Since the
 * Phase 2 codec migration (see ``api/codec.ts``), `getFilingContext` no
 * longer relies on the global axios camelizer for this shape: the Zod
 * schema below reads BOTH spellings via ``dualKey`` (snake_case is the
 * BE / `filing_context_snapshot` truth; camelCase is what the legacy
 * interceptor delivers) and normalizes onto this camelCase contract. The
 * payload therefore parses identically whether it arrived through the
 * camelizing interceptor or a future raw/`codecGet` path — same
 * justified canonical-camelCase exception as the tool-trace reducer.
 */
import { z } from "zod";

import { dualKey, isObject, toSnakeDeep } from "@/api/codec";

// ---------------------------------------------------------------------------
// Enums — backend-owned. Keep exhaustive lists, but consumers should fall back
// gracefully when a new value shows up (additive contract).
// ---------------------------------------------------------------------------

/**
 * Backend `filing_context.status.bucket` enum. These are **semantic** buckets
 * about where the filing sits in the workflow, emitted directly by the API.
 *
 * Distinct from the FE-visual buckets in `statusTaxonomy.ts`
 * (`action_needed | in_review | with_rdp | with_dob | complete | exception`)
 * which map to tones/icons. Per the architecture doc §2.3 hybrid model, both
 * live side by side — this one from the backend, the FE one for presentation.
 */
export type StatusBucket =
  | "terminal_exempt"
  | "terminal_success"
  | "awaiting_owner"
  | "awaiting_rdp"
  | "awaiting_cc"
  | "awaiting_dob"
  | "in_progress"
  | "terminal_failed";

export type LawCode = "LL97" | "LL88" | "LL84";

export type BlockedBy = "owner" | "rdp" | "carbonclear" | "dob" | null;

export type ActionActor = "owner" | "rdp" | "carbonclear" | "dob";

// ---------------------------------------------------------------------------
// Sub-shapes
// ---------------------------------------------------------------------------

export interface FilingContextLaw {
  /** REQUIRED. Machine-readable law identifier. */
  code: LawCode;
  /** Plain-language name, owner-safe. e.g. "Local Law 97". */
  displayName?: string;
  shortName?: string;
  jurisdiction?: string;
  /** Does this law support a paid extension? */
  isExtensible?: boolean;
  citationUri?: string | null;
  compliancePeriodLabel?: string | null;
  /** DOB fee for the base filing, in cents. `null` when not applicable. */
  dobPaymentFeeCents?: number | null;
  dobPaymentCodePrefix?: string | null;
}

export interface FilingContextCycle {
  /** REQUIRED. Reporting year (the data year, not the deadline year). */
  calendarYear: number;
  /** REQUIRED. ISO-8601 (YYYY-MM-DD) standard filing deadline for this cycle. */
  standardDeadline: string;
  gatingCblYear?: number;
  /** ISO-8601 extended deadline; `null` when the law is not extensible. */
  extendedDeadline?: string | null;
  /** Extension fee in cents. `null` when the law is not extensible. */
  extensionFeeCents?: number | null;
  extensionFeeSource?: string | null;
}

export interface FilingContextSurfaceReason {
  category?: string;
  heading?: string;
  body?: string;
  cta?: {
    label?: string;
    href?: string;
    actor?: ActionActor;
  } | null;
  meta?: Record<string, unknown>;
}

export interface FilingContextStatus {
  /** REQUIRED. Raw backend status code (e.g. 9005). */
  code: number;
  /** REQUIRED. Full human-readable label for this status. */
  label: string;
  /** REQUIRED. Machine-readable workflow bucket. */
  bucket: StatusBucket;
  shortLabel?: string;
  /** Who is blocking progression? `null` for terminal states. */
  blockedBy?: BlockedBy;
  /** e.g. "prior_year_not_filed" when an upstream cycle blocks this one. */
  blockedReason?: string | null;
  isFinal?: boolean;
  isException?: boolean;
  surfaceReason?: FilingContextSurfaceReason | null;
}

export interface FilingContextNextAction {
  kind?: string;
  label?: string;
  href?: string;
  actor?: ActionActor;
  blockedReason?: string | null;
}

export interface FilingContextCblProvenance {
  cblYear?: number;
  loadedAt?: string;
  sha256?: string;
  filename?: string;
  auditLogHref?: string;
}

// ---------------------------------------------------------------------------
// Top-level shape
// ---------------------------------------------------------------------------

export interface FilingContext {
  version?: number;
  law: FilingContextLaw;
  cycle: FilingContextCycle;
  status: FilingContextStatus;
  nextAction?: FilingContextNextAction | null;
  cblProvenance?: FilingContextCblProvenance | null;
  /**
   * The BE builder emits `surface_reason` at the TOP level (see
   * `audit/services/filing_context.py::build_filing_context`), not under
   * `status` — both locations are modeled because the interface
   * historically declared `status.surfaceReason`. No consumer reads
   * either yet; the schema preserves both so whichever the BE settles
   * on keeps parsing.
   */
  surfaceReason?: FilingContextSurfaceReason | null;
  /** NYC-assigned default pathway (CP0..CP4) from the CBL row. Only present on LL97 contexts in CBL preview responses. */
  previewPathway?: string | null;
}

// ---------------------------------------------------------------------------
// Codec (Phase 2 of the option-2 migration — see ``api/codec.ts``)
// ---------------------------------------------------------------------------

/**
 * Read the surface-reason block tolerant of either key spelling. The
 * nested ``cta`` keys (label/href/actor) are single words — identical in
 * both spellings. ``meta`` is an opaque BE-owned record whose canonical
 * key shape is snake_case (the Python author's spelling); it rides
 * ``toSnakeDeep`` so a payload the legacy interceptor already camelized
 * collapses back onto the same keys the raw path delivers.
 */
/**
 * Read a nullable sub-block: explicit ``null`` is preserved (the BE
 * meaningfully emits it), a non-object becomes ``undefined``, and an
 * object is handed to ``build`` for field extraction.
 */
const readNullableBlock = <T>(
  raw: unknown,
  build: (obj: Record<string, unknown>) => T,
): T | null | undefined => {
  if (raw === null) return null;
  if (!isObject(raw)) return undefined;
  return build(raw);
};

const readSurfaceReason = (
  raw: unknown,
): FilingContextSurfaceReason | null | undefined => {
  if (raw === null) return null;
  if (!isObject(raw)) return undefined;
  const meta = dualKey(raw, "meta");
  return {
    category: dualKey(raw, "category") as string | undefined,
    heading: dualKey(raw, "heading") as string | undefined,
    body: dualKey(raw, "body") as string | undefined,
    cta: dualKey(raw, "cta") as FilingContextSurfaceReason["cta"],
    meta: (isObject(meta) ? toSnakeDeep(meta) : meta) as
      | Record<string, unknown>
      | undefined,
  };
};

/**
 * Transport-agnostic parser for the `filing_context` payload (live-built
 * OR the frozen `filing_context_snapshot` JSONField — same shape, see
 * `audit/services/filing_context.py`). Reads every field via ``dualKey``
 * (snake first — the BE/snapshot truth — camel fall-back for payloads
 * that rode the legacy axios camelizer) and emits the canonical
 * camelCase `FilingContext` consumer contract.
 *
 * Parses to `undefined` (NOT a throw) when the required core is missing
 * — preserving the additive-contract tolerance `getFilingContext` has
 * always promised its 20+ call sites.
 */
export const filingContextSchema: z.ZodType<
  FilingContext | undefined,
  z.ZodTypeDef,
  unknown
> = z.unknown().transform((raw): FilingContext | undefined => {
  if (!isObject(raw)) return undefined;

  const lawRaw = dualKey(raw, "law");
  const cycleRaw = dualKey(raw, "cycle");
  const statusRaw = dualKey(raw, "status");
  if (!isObject(lawRaw) || !isObject(cycleRaw) || !isObject(statusRaw))
    return undefined;

  // Required fields must be present for the shape to be usable
  // downstream (same guard the pre-codec implementation enforced).
  const lawCode = dualKey(lawRaw, "code");
  const calendarYear = dualKey(cycleRaw, "calendar_year");
  const standardDeadline = dualKey(cycleRaw, "standard_deadline");
  const statusCode = dualKey(statusRaw, "code");
  const statusLabel = dualKey(statusRaw, "label");
  const bucket = dualKey(statusRaw, "bucket");
  if (typeof lawCode !== "string") return undefined;
  if (typeof calendarYear !== "number" || typeof standardDeadline !== "string")
    return undefined;
  if (
    typeof statusCode !== "number" ||
    typeof statusLabel !== "string" ||
    typeof bucket !== "string"
  ) {
    return undefined;
  }

  const nextActionRaw = dualKey(raw, "next_action");
  const cblRaw = dualKey(raw, "cbl_provenance");

  return {
    version: dualKey(raw, "version") as number | undefined,
    law: {
      code: lawCode as LawCode,
      displayName: dualKey(lawRaw, "display_name") as string | undefined,
      shortName: dualKey(lawRaw, "short_name") as string | undefined,
      jurisdiction: dualKey(lawRaw, "jurisdiction") as string | undefined,
      isExtensible: dualKey(lawRaw, "is_extensible") as boolean | undefined,
      citationUri: dualKey(lawRaw, "citation_uri") as string | null | undefined,
      compliancePeriodLabel: dualKey(lawRaw, "compliance_period_label") as
        | string
        | null
        | undefined,
      dobPaymentFeeCents: dualKey(lawRaw, "dob_payment_fee_cents") as
        | number
        | null
        | undefined,
      dobPaymentCodePrefix: dualKey(lawRaw, "dob_payment_code_prefix") as
        | string
        | null
        | undefined,
    },
    cycle: {
      calendarYear,
      standardDeadline,
      gatingCblYear: dualKey(cycleRaw, "gating_cbl_year") as number | undefined,
      extendedDeadline: dualKey(cycleRaw, "extended_deadline") as
        | string
        | null
        | undefined,
      extensionFeeCents: dualKey(cycleRaw, "extension_fee_cents") as
        | number
        | null
        | undefined,
      extensionFeeSource: dualKey(cycleRaw, "extension_fee_source") as
        | string
        | null
        | undefined,
    },
    status: {
      code: statusCode,
      label: statusLabel,
      bucket: bucket as StatusBucket,
      shortLabel: dualKey(statusRaw, "short_label") as string | undefined,
      blockedBy: dualKey(statusRaw, "blocked_by") as BlockedBy | undefined,
      blockedReason: dualKey(statusRaw, "blocked_reason") as
        | string
        | null
        | undefined,
      isFinal: dualKey(statusRaw, "is_final") as boolean | undefined,
      isException: dualKey(statusRaw, "is_exception") as boolean | undefined,
      surfaceReason: readSurfaceReason(dualKey(statusRaw, "surface_reason")),
    },
    nextAction: readNullableBlock(nextActionRaw, (obj) => ({
      kind: dualKey(obj, "kind") as string | undefined,
      label: dualKey(obj, "label") as string | undefined,
      href: dualKey(obj, "href") as string | undefined,
      actor: dualKey(obj, "actor") as ActionActor | undefined,
      blockedReason: dualKey(obj, "blocked_reason") as
        | string
        | null
        | undefined,
    })),
    cblProvenance: readNullableBlock(cblRaw, (obj) => ({
      cblYear: dualKey(obj, "cbl_year") as number | undefined,
      loadedAt: dualKey(obj, "loaded_at") as string | undefined,
      sha256: dualKey(obj, "sha256") as string | undefined,
      filename: dualKey(obj, "filename") as string | undefined,
      auditLogHref: dualKey(obj, "audit_log_href") as string | undefined,
    })),
    surfaceReason: readSurfaceReason(dualKey(raw, "surface_reason")),
    previewPathway: dualKey(raw, "preview_pathway") as
      | string
      | null
      | undefined,
  };
});

// ---------------------------------------------------------------------------
// Utility: defensive access
// ---------------------------------------------------------------------------

/**
 * Narrow an unknown submission object to its `FilingContext` if the
 * embedded payload has the minimum required shape. Otherwise return
 * `undefined`.
 *
 * This is the single point of tolerance for stale payloads that predate
 * Phase 1 (no `filingContext` present) or that lost the field in transport.
 * Every consumer should call this rather than accessing `submission.filingContext`
 * directly so we have one place to extend defensively.
 *
 * Phase 2 codec: the payload is parsed through `filingContextSchema`, so
 * it resolves identically whether the submission arrived through the
 * legacy camelizing interceptor (`filingContext`, camel keys) or a raw
 * `codecGet` path (`filing_context`, snake keys).
 */
export function getFilingContext(
  submission: unknown,
): FilingContext | undefined {
  if (!isObject(submission)) return undefined;
  return filingContextSchema.parse(dualKey(submission, "filing_context"));
}
